/**
 * PlayerSnapIndicator - Visual indicator for snapping to player positions
 * Shows a pulsing blue circle when a drawing endpoint is near a player
 */

import { PLAYER_RADIUS_FEET } from '@/constants'
import type { FieldCoordinateSystem } from '@/utils'
import type { Coordinate } from '@/types'

interface PlayerSnapIndicatorProps {
	/** Field coordinate system for pixel conversion */
	coordSystem: FieldCoordinateSystem
	/** The player position to snap to */
	playerPosition: Coordinate
	/** Optional CSS class for animation control */
	className?: string
}

/**
 * Renders a pulsing blue circle indicator at the player snap target
 * Used when dragging drawing endpoints or whole drawings near players
 */
export function PlayerSnapIndicator({
										coordSystem,
										playerPosition,
										className = 'animate-pulse',
									}: PlayerSnapIndicatorProps) {
	const pixelPos = coordSystem.feetToPixels(playerPosition.x, playerPosition.y)
	const radius = PLAYER_RADIUS_FEET * coordSystem.scale + 6

	return (
		<circle
			cx={pixelPos.x}
			cy={pixelPos.y}
			r={radius}
			fill="var(--snap-player-fill, rgba(59,130,246,0.2))"
			stroke="var(--snap-player-stroke, #3b82f6)"
			strokeWidth={3}
			pointerEvents="none"
			className={className}
			style={{
				filter: 'drop-shadow(0 0 6px var(--snap-player-glow, rgba(59,130,246,0.6)))',
			}}
		/>
	)
}
